<template>
  <v-row v-for="itemRow in setItems">
    <v-col v-bind="item.questionModel.layout" v-for="item in itemRow">
      <QuestionSelector
        :question-model="item.questionModel"
        :answer="item.answers as unknown as object ?? null"
        :isConditionFulfilled="conditionsAreFulfilled(item)"
        @q-event="(val) => updateAnswers(val, item.questionModel.question.id)"
        @is-valid="emitIsValid"
      />
    </v-col>
  </v-row>
</template>

<script setup lang="ts">
import { computed } from "vue";
import QuestionSelector from "@/components/questionnaire/QuestionSelector.vue";
import type {
  QuestionnaireItemDto,
  QuestionnaireItemSetDto,
} from "@/models/dto/questionnaireItemSetDto";
import {
  type QuestionConditionDependency,
  QuestionConditionOperator,
} from "@/models/dto/questionConditions";
import { AnswersCheckboxModel } from "@/models/questions/checkbox";
import BooleanWithDetails from "@/models/questions/booleanWithDetails";

const props = defineProps<{
  set: QuestionnaireItemSetDto;
  contextId: string;
}>();

const emit = defineEmits<{
  (e: "answerUpdated", value: object, qId: string): void;
  (e: "isValid", isValid: boolean, invalidField: string): void;
}>();

const setItems = computed(() => props.set.items);

const conditionsAreFulfilled = computed(
  () => (item: QuestionnaireItemDto) => checkIfConditionsAreFulfilled(item)
);

function updateAnswers(value: object, qId: string): void {
  emit("answerUpdated", value, qId);
}

function emitIsValid(isValid: boolean, invalidField: string): void {
  emit("isValid", isValid, invalidField);
}

function checkIfConditionsAreFulfilled(item: QuestionnaireItemDto): boolean {
  //If there are no conditions, we should show the question
  if (
    !item.questionModel.conditions ||
    item.questionModel.conditions.dependencies.length === 0
  ) {
    return true;
  }

  switch (item.questionModel.conditions.operator) {
    case QuestionConditionOperator.SINGLE: {
      const condition = item.questionModel.conditions.dependencies[0];
      return answerIsFulfilled(condition);
    }
    case QuestionConditionOperator.AND: {
      return item.questionModel.conditions.dependencies
        .map((x) => answerIsFulfilled(x))
        .every((fulfilledCondition) => fulfilledCondition);
    }
    case QuestionConditionOperator.OR: {
      return item.questionModel.conditions.dependencies
        .map((x) => answerIsFulfilled(x))
        .some((fulfilledCondition) => fulfilledCondition);
    }
    default: {
      throw new Error(
        `Question Condition Operator: '${item.questionModel.conditions.operator}' not recognized.`
      );
    }
  }
}

function answerIsFulfilled(condition: QuestionConditionDependency): boolean {
  const dependentQuestion = findQuestionWithId(condition.dependsOn);

  //If we can not find dependant question or it has no answers. We still return true if answer is optional.
  if (!dependentQuestion || !dependentQuestion.answers) {
    return !condition.requiresAnswer;
  }

  //We also have to check if the dependant question has all its conditions fulfilled.
  if (!checkIfConditionsAreFulfilled(dependentQuestion)) {
    return false;
  }

  if (
    Array.isArray(dependentQuestion.answers) &&
    dependentQuestion.answers.every((x) => x as AnswersCheckboxModel)
  ) {
    const answers = dependentQuestion.answers as Array<AnswersCheckboxModel>;

    if (condition.dependantValue == "UncheckedOrNoneOfTheAbove") {
      const anyUnchecked = answers
        .filter((x) => x.label.toLowerCase() !== "none of the above")
        .some((x) => !x.checked);

      const noneOfTheAbove =
        answers.find((x) => x.label.toLowerCase() === "none of the above")
          ?.checked ?? false;

      return anyUnchecked || noneOfTheAbove;
    }

    const dependantAnswer = answers.find(
      (v) => v.label === condition.dependantValue
    );

    return dependantAnswer?.checked ?? false;
  }

  if (
    (dependentQuestion.answers as BooleanWithDetails) &&
    (dependentQuestion.answers as BooleanWithDetails).boolean
  ) {
    const answer = dependentQuestion.answers as BooleanWithDetails;
    return answer.boolean === condition.dependantValue;
  }

  //Lastly check if condition is fulfilled
  return dependentQuestion.answers.toString() === condition.dependantValue;
}

function findQuestionWithId(questionId: string): QuestionnaireItemDto | null {
  for (const questionSet of setItems.value) {
    const foundQuestion = questionSet.find(
      (question) => question.questionModel.question.id === questionId
    );

    if (foundQuestion) {
      return foundQuestion;
    }
  }
  return null;
}
</script>
